import loadable from '@loadable/component';
import { logError } from 'fergy-core-react-logging';
import { type FunctionComponent, useEffect, useRef } from 'react';
import { REGEX_NEWLINES, REGEX_WHITE_SPACE } from '../../../../constants/regular-expression';
import { SEARCH_ROUTE } from '../../../constants/links';
import { MAX_QUERY_LENGTH } from '../../../constants/search-bar';
import { useSearchBarReducerContext } from '../../../contexts/search-bar/search-bar.context';
import { generateDataSelector } from '../../../helpers/general-helper/general-helper';
import { handleKeys } from '../../../helpers/keyboard/keyboard.helper';
import { preloadComponentForUrl } from '../../../helpers/routing/route.helpers';
import { useControlledFocus } from '../../../hooks/general/controlled-focus.hooks';
import { useNavigation } from '../../../hooks/navigation/navigation.hooks';
import { ClickableElement } from '../../buttons/clickable-element/clickable-element.component';
import { IconButton } from '../../buttons/icon-button/icon-button.component';
import { Overlay } from '../../common-components/overlay/overlay.component';
import ChevronLeftIcon from '../../svgs/chevron-left.svg';
import CloseIcon from '../../svgs/close.svg';
import SearchIcon from '../../svgs/search.svg';
import { button, cancelButton, focusWithin, hide, searchContainer, textarea, wide } from './search-bar.css';

const LoadableSearchBarResults = loadable(
	() => import(/* webpackChunkName: "search-bar-results" */ '../search-bar-results/search-bar-results.component'),
	{ resolveComponent: ({ SearchBarResults }) => SearchBarResults }
);

// We will dynamically load the search bar controller module when needed.
type SearchBarControllerModule = typeof import('./search-bar-controller');
let SearchBarController: SearchBarControllerModule | undefined;

export async function loadSearchBarController(): Promise<void> {
	if (!SearchBarController) {
		try {
			SearchBarController = await import(/* webpackChunkName: "search-bar-controller" */ './search-bar-controller');
		} catch (error) {
			logError(error);
		}
	}
}

function preloadTypeaheadModules() {
	void loadSearchBarController();
	LoadableSearchBarResults.preload();
}

let searchPagePreloaded = false;

function preloadSearchPage() {
	if (!searchPagePreloaded) {
		preloadComponentForUrl(SEARCH_ROUTE);
		searchPagePreloaded = true;
	}
}

export type SearchBarProps = {
	onTypeaheadOpenChanged?: (isOpen: boolean) => void;
	placeholder?: string;
	inDrawer?: boolean;
};

/**
 * Renders a search input that fetches recent searches and typeahead suggested products and terms and displays them in a popup below.
 */
export const SearchBar: FunctionComponent<SearchBarProps> = ({
	onTypeaheadOpenChanged,
	placeholder = 'What are you shopping for?',
	inDrawer = false
}) => {
	const navigate = useNavigation();
	const [state, dispatch] = useSearchBarReducerContext();

	const inputRef = useRef<HTMLInputElement>(null);
	const textAreaRef = useRef<HTMLTextAreaElement>(null);
	useControlledFocus(inputRef, state.inputFocused);
	useControlledFocus(textAreaRef, state.inputFocused);

	const handleFormSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
		event.preventDefault();
		const searchTerm = state.displayValue.replace(REGEX_WHITE_SPACE, ' ').trim();
		if (!searchTerm) {
			return;
		}
		dispatch({ type: 'typeaheadClosed' });
		navigate(`/search?term=${encodeURIComponent(searchTerm)}`);
	};
	const handleInputFocus = () => {
		preloadTypeaheadModules();
		preloadSearchPage();
		if (!state.inputFocused) {
			dispatch({ type: 'inputFocused' });
		}
	};
	const handleInputBlur = () => {
		if (state.inputFocused) {
			dispatch({ type: 'inputBlurred' });
			if (textAreaRef.current) {
				textAreaRef.current.scrollTo(0, 0);
			}
		}
	};
	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const cleanValue = event.target.value.replace(REGEX_NEWLINES, ' ');
		dispatch({ type: 'inputChanged', value: cleanValue });
	};
	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (!SearchBarController) {
			void loadSearchBarController();
		}
		SearchBarController?.handleKeyDown(state, dispatch, event);
	};
	const handleCloseTypeahead = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
		event.preventDefault();
		event.currentTarget.blur();
		dispatch({ type: 'typeaheadClosed' });
	};
	const handleClearClick = () => dispatch({ type: 'inputCleared' });
	const handleTextareaKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
		// Prevent Enter key from inserting newline, and instead submit the search form.
		if (event.key === 'Enter') {
			event.preventDefault();
			event.stopPropagation();
			event.currentTarget.form?.requestSubmit();
		}
	};

	useEffect(() => {
		onTypeaheadOpenChanged?.(state.showResults);
	}, [onTypeaheadOpenChanged, state.showResults]);

	// Move textarea caret to end on mount
	useEffect(() => {
		if (textAreaRef.current) {
			textAreaRef.current.selectionStart = textAreaRef.current.value.length;
		}
	}, []);

	// Expand and contract the height of the textarea to fit the text value inside it.
	useEffect(() => {
		const textAreaElement = textAreaRef.current;
		if (textAreaElement) {
			setTimeout(() => {
				textAreaElement.style.height = 'auto'; // Shrink to handle removed text.
				textAreaElement.style.height = Math.min(textAreaElement.scrollHeight, 76) + 'px';
			}, 0);
		}
	}, [state.displayValue]);

	const wideClass = state.inputFocused || state.showResults ? `${wide}` : '';
	const zIndex = state.showResults ? 'z-3' : 'z-1'; // Raise z-index when results are showing so it is above the nav bar.
	const formZIndex = state.showResults ? 'z-4' : ''; // Use higher z-index than the overlay so search input clicks aren't blocked.

	return (
		// eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
		<div
			role="search"
			className={`fl-ns w-100 ${zIndex} ${searchContainer} ${wideClass} ${inDrawer ? '' : 'relative mw-100'}`}
			onKeyDown={handleKeyDown}>
			{/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions*/}
			<form
				action={SEARCH_ROUTE}
				onSubmit={handleFormSubmit}
				className={`relative flex items-center ${formZIndex}`}
				aria-label="site wide search form"
				onMouseEnter={preloadTypeaheadModules}>
				<div className={`bg-theme-white b--theme-grey-light br2 flex flex-nowrap flex-auto ba items-start ${focusWithin}`}>
					{inDrawer && (
						<IconButton
							ariaLabel="Close search"
							size="ICON"
							buttonStyle="ICON"
							className={`bn outline-0 ${button}`}
							onClick={handleCloseTypeahead}>
							<ChevronLeftIcon className="db f5" />
						</IconButton>
					)}
					{/* Use expanding textarea inside the mobile drawer */}
					{inDrawer ? (
						<textarea
							name="term"
							aria-label="Search"
							role="searchbox"
							rows={1}
							data-automation={generateDataSelector('textarea', 'search')}
							value={state.displayValue}
							onFocus={handleInputFocus}
							onBlur={handleInputBlur}
							onChange={handleInputChange}
							onKeyDown={handleTextareaKeyDown}
							placeholder={placeholder}
							data-testid="SearchInput"
							autoComplete="off"
							autoCapitalize="off"
							autoCorrect="off"
							maxLength={MAX_QUERY_LENGTH}
							className={`${textarea} input-reset mv1 pv2 pl1 pr0 w-100 f5 sans-serif theme-grey-darker lh-title bn outline-0`}
							required={true}
							ref={textAreaRef}
						/>
					) : (
						<input
							name="term"
							aria-label="Search"
							type="text"
							role="searchbox"
							data-automation={generateDataSelector('input', 'search')}
							value={state.displayValue}
							onFocus={handleInputFocus}
							onBlur={handleInputBlur}
							onChange={handleInputChange}
							placeholder={placeholder}
							data-testid="SearchInput"
							autoComplete="off"
							autoCapitalize="off"
							autoCorrect="off"
							maxLength={MAX_QUERY_LENGTH}
							className="input-reset input br2 f5 lh-title bn w-100 truncate theme-grey-darker outline-0 pl3 pr0"
							required={true}
							ref={inputRef}
						/>
					)}
					<div className="flex-shrink-0">
						<div className="flex">
							{state.displayValue && (
								<IconButton
									ariaLabel="Clear search"
									size="ICON"
									buttonStyle="ICON"
									className={`bn outline-0 ${button}`}
									onClick={handleClearClick}>
									<CloseIcon className="db f5" />
								</IconButton>
							)}
							<Divider />
							<IconButton
								buttonType="submit"
								ariaLabel="Submit search"
								size="ICON"
								buttonStyle="ICON"
								className={`bn outline-0 ${button}`}>
								<SearchIcon className="db f4" data-testid="SearchIcon" />
							</IconButton>
						</div>
						{inDrawer && state.displayValue.length >= 75 && (
							<div className="f6 tr pr2 pb2">
								{state.displayValue.length} / <span className="fw6">{MAX_QUERY_LENGTH}</span>
							</div>
						)}
					</div>
				</div>
				{!inDrawer && (
					<ClickableElement
						ariaLabel="Close search suggestions"
						onClick={handleCloseTypeahead}
						onKeyDown={handleKeys([' ', 'Enter'], handleCloseTypeahead)}
						className={`${cancelButton} ${
							state.showResults ? '' : hide
						} flex items-center dn-ns input nowrap ph2 ml1 f5 lh-title br2 active-bg-theme-grey-darker shadow-secondary fw7 bn theme-primary-dark active-theme-primary-darker`}>
						Cancel
					</ClickableElement>
				)}
			</form>
			{!inDrawer && state.showResults && (
				<>
					<Overlay style="dark" hasTransparentHeader={true} onClick={handleCloseTypeahead} />
					<LoadableSearchBarResults state={state} dispatch={dispatch} />
				</>
			)}
		</div>
	);
};

const Divider: FunctionComponent = () => (
	<div className={button}>
		<div className="bl b--theme-grey-light" style={{ height: '1.5rem' }} />
	</div>
);
